import type { Locale } from "./types";

export type ServiceCatalogPackageKey = "start" | "growth" | "operator" | "migration_cleanup" | "custom";

export type ServiceCatalogPackage = {
  key: ServiceCatalogPackageKey;
  label: Record<Locale, string>;
  fit: Record<Locale, string>;
  typicalWorkstreams: string[];
  platformPosture: Record<Locale, string>;
  reviewPosture: Record<Locale, string>;
};

export type ServiceCatalogWorkstream = {
  key:
    | "monthly_bookkeeping"
    | "bank_matching"
    | "vat_reporting"
    | "ap_ar_review"
    | "reports"
    | "sie_migration"
    | "cleanup"
    | "fixed_assets"
    | "annual_artifacts"
    | "scope_control";
  label: Record<Locale, string>;
  summary: Record<Locale, string>;
};

export type SubscriptionAgentCreditCard = {
  key:
    | "subscription_access"
    | "agent_credit_consumption"
    | "estimate_output"
    | "dynamicmail_billing_handoff"
    | "scope_control_boundary";
  title: string;
  body: string;
};

export type SubscriptionAgentCreditContent = {
  eyebrow: string;
  title: string;
  lead: string;
  cards: SubscriptionAgentCreditCard[];
  billingHandoffTitle: string;
  billingHandoffBody: string;
  nonBindingNote: string;
  guardrails: string[];
};

export const serviceCatalogWorkstreams: ServiceCatalogWorkstream[] = [
  {
    key: "monthly_bookkeeping",
    label: { sv: "Löpande bokföring", en: "Monthly bookkeeping" },
    summary: {
      sv: "Transaktioner, kvitton, kundfakturor, leverantörsunderlag och momsperioder i ett spårbart flöde.",
      en: "Transactions, receipts, sales invoices, supplier material, and VAT periods in a traceable workflow."
    }
  },
  {
    key: "bank_matching",
    label: { sv: "Bankflöde och matchning", en: "Bank flow and matching" },
    summary: {
      sv: "Bankfiler, manuella importer och konfigurerade källor som Revolut kontrolleras mot konto, moms och verifikation.",
      en: "Bank files, manual imports, and configured sources such as Revolut are checked against accounts, VAT, and journals."
    }
  },
  {
    key: "vat_reporting",
    label: { sv: "Momsrapportstöd", en: "VAT reporting support" },
    summary: {
      sv: "Momsperioder kan förberedas, kontrolleras och paketeras som spårbara artefakter där flödet är konfigurerat.",
      en: "VAT periods can be prepared, checked, and packaged as traceable artifacts where the workflow is configured."
    }
  },
  {
    key: "ap_ar_review",
    label: { sv: "AP/AR-reskontra", en: "AP/AR ledger setup" },
    summary: {
      sv: "Kund- och leverantörsreskontra, betalstatus, kandidater och verifikationslänkning kontrolleras som separat arbetsström.",
      en: "Customer and supplier ledgers, payment status, candidates, and journal links are checked as a separate workstream."
    }
  },
  {
    key: "reports",
    label: { sv: "Rapporter", en: "Reports" },
    summary: {
      sv: "Balans, resultat, huvudbok, verifikationslista, kontosaldon och rapportregister kan användas i uppskattnings- och kontrollflöden.",
      en: "Balance sheet, profit and loss, general ledger, journal list, account balances, and report register outputs can support estimate and control flows."
    }
  },
  {
    key: "sie_migration",
    label: { sv: "SIE-migrering", en: "SIE migration" },
    summary: {
      sv: "SIE import/export, dubblettkontroll och migreringskontroll hanteras som omfattningsstyrt arbete.",
      en: "SIE import/export, duplicate checks, and migration controls are handled as scope-controlled work."
    }
  },
  {
    key: "cleanup",
    label: { sv: "Städning och ikappföring", en: "Cleanup and catch-up" },
    summary: {
      sv: "Historiska perioder, saknade underlag, korrigeringar och avvikelser styrs till guidad kontroll.",
      en: "Historical periods, missing evidence, corrections, and discrepancies route to guided onboarding."
    }
  },
  {
    key: "fixed_assets",
    label: { sv: "Anläggningar och avskrivning", en: "Fixed assets and depreciation" },
    summary: {
      sv: "Anläggningsregister, avskrivningsstöd och avstämning behandlas konservativt med omfattningssignaler.",
      en: "Fixed asset registers, depreciation support, and reconciliation are treated conservatively with scope signals."
    }
  },
  {
    key: "annual_artifacts",
    label: { sv: "Års- och rapportartefakter", en: "Annual and report artifacts" },
    summary: {
      sv: "Bokslut, årsarbete och rapportpaket förbereds som underlag utan inlämnings- eller regelefterlevnadsgarantier.",
      en: "Year-end work and report packages are prepared as evidence without filing or compliance guarantees."
    }
  },
  {
    key: "scope_control",
    label: { sv: "Omfattningskontroll", en: "Scope control" },
    summary: {
      sv: "Migrering, städning, bokslut, lön, okända källor och riskmarkörer kontrolleras innan omfattningen bekräftas.",
      en: "Migration, cleanup, year-end, payroll, unknown sources, and risk markers are checked before scope is confirmed."
    }
  }
];

export const serviceCatalogPackages: ServiceCatalogPackage[] = [
  {
    key: "start",
    label: { sv: "Start", en: "Start" },
    fit: {
      sv: "Låg volym, enkel moms och tydliga källor för självstyrd eller lätt hybridintroduktion.",
      en: "Lower volume, simple VAT, and clear sources for self-directed or light hybrid onboarding."
    },
    typicalWorkstreams: ["monthly_bookkeeping", "bank_matching", "vat_reporting", "reports"],
    platformPosture: {
      sv: "DynamicBooks Basic kan passa när intensitet i Data Rooms och agentkörningar är låga.",
      en: "DynamicBooks Basic may fit when data-room intensity and agent runs are low."
    },
    reviewPosture: {
      sv: "Ingen krävd kontroll om inga riskflaggor finns.",
      en: "No required scope check when no risk flags are present."
    }
  },
  {
    key: "growth",
    label: { sv: "Tillväxt", en: "Growth" },
    fit: {
      sv: "Återkommande volym, AP/AR, moms och rapportbehov med mer spårbar aktivitet.",
      en: "Recurring volume, AP/AR, VAT, and reporting needs with more traceable activity."
    },
    typicalWorkstreams: ["monthly_bookkeeping", "bank_matching", "vat_reporting", "ap_ar_review", "reports"],
    platformPosture: {
      sv: "DynamicBooks Business är ofta rimlig när flera arbetsströmmar och kontrollpunkter behövs.",
      en: "DynamicBooks Business is often reasonable when multiple workstreams and checkpoints are needed."
    },
    reviewPosture: {
      sv: "Hybrid leverans kan passa när undantag och periodsteg ska kontrolleras.",
      en: "Hybrid delivery may fit when exceptions and period steps need checks."
    }
  },
  {
    key: "operator",
    label: { sv: "Operatör", en: "Operator" },
    fit: {
      sv: "Högre volym, flera källor, anläggningstillgångar eller mer rapportintensiv drift.",
      en: "Higher volume, multiple sources, fixed assets, or more report-intensive operations."
    },
    typicalWorkstreams: ["monthly_bookkeeping", "bank_matching", "vat_reporting", "ap_ar_review", "fixed_assets", "reports"],
    platformPosture: {
      sv: "DynamicBooks Premium kan passa för starkare Data Rooms- och agentintensitet.",
      en: "DynamicBooks Premium may fit stronger data-room and agent-run intensity."
    },
    reviewPosture: {
      sv: "Utökad uppstart rekommenderas för återkommande kontroll och ansvarskänsliga steg.",
      en: "Guided onboarding is recommended for recurring control and responsibility-sensitive steps."
    }
  },
  {
    key: "migration_cleanup",
    label: { sv: "Migrering och städning", en: "Migration/Cleanup" },
    fit: {
      sv: "SIE, historik, städning, bokslut, avvikelser eller första periodens uppbyggnad.",
      en: "SIE, history, cleanup, year-end work, discrepancies, or first-period setup."
    },
    typicalWorkstreams: ["sie_migration", "cleanup", "bank_matching", "vat_reporting", "annual_artifacts", "scope_control"],
    platformPosture: {
      sv: "DynamicBooks Business eller Enterprise väljs efter intensitet i Data Rooms och omfattningsbehov.",
      en: "DynamicBooks Business or Enterprise depends on data-room intensity and scope needs."
    },
    reviewPosture: {
      sv: "Omfattningskontroll krävs innan ansvarsnivå bekräftas.",
      en: "Scope control is required before scope and responsibility level are confirmed."
    }
  },
  {
    key: "custom",
    label: { sv: "Anpassad", en: "Custom" },
    fit: {
      sv: "Mycket hög volym, oklar källtäckning, riskmarkörer, flera entiteter eller ansvarskänsliga förväntningar.",
      en: "Very high volume, unclear source coverage, risk markers, multiple entities, or responsibility-sensitive expectations."
    },
    typicalWorkstreams: ["scope_control", "reports", "bank_matching"],
    platformPosture: {
      sv: "Anpassad kontroll väljs innan plattform, Agent credits och serviceplan presenteras.",
      en: "Custom onboarding is selected before platform, credits, and service plan are presented."
    },
    reviewPosture: {
      sv: "Omfattning sätts efter kontroll och stegvisa torrkörningar i Data Rooms.",
      en: "Scope is set after checks and staged data-room dry-runs."
    }
  }
];

export const serviceCatalogGuardrails = {
  sv: [
    "Katalogen innehåller inga SEK-belopp och är inte ett faktureringsbeslut.",
    "Publik text ska beskriva preliminär uppskattning, servicepaket, Agent credits och omfattningssignaler.",
    "Agenter förbereder och föreslår; omfattning och ansvar bekräftas innan arbete startar."
  ],
  en: [
    "The catalog contains no SEK amounts and is not a billing decision.",
    "Public copy should describe preliminary estimates, service packages, agent credits, and scope signals.",
    "Agents prepare and suggest; scope control is used when scope, responsibility, or risk requires it."
  ]
} satisfies Record<Locale, string[]>;

export const subscriptionAgentCreditContent = {
  sv: {
    eyebrow: "Åtkomst och Agent credits",
    title: "Abonnemang ger åtkomst. Agent credits beskriver körkapacitet.",
    lead:
      "DynamicBooks visar preliminär åtkomstnivå och förväntad Agent credits-intensitet för bokföring i Data Rooms. Det är uppskattningspositionering, inte betalning eller slutlig fakturering.",
    cards: [
      {
        key: "subscription_access",
        title: "Abonnemangsåtkomst",
        body:
          "Föreslagen DynamicMail-nivå speglar Data Rooms, arbetsströmmar, kontrollpunkter och bokföringsagentens intensitet."
      },
      {
        key: "agent_credit_consumption",
        title: "Agent credits-förbrukning",
        body:
          "Månadscredits visas i DynamicMail ledger-enheter och uppskattas från faktiska agentkörningar, transaktionsvolym, momsperiod, AP/AR, SIE, städning, bokslut och anläggningar."
      },
      {
        key: "estimate_output",
        title: "Uppskattningsresultat",
        body:
          "Resultatet visar föreslagen åtkomstnivå, förväntade månadscredits och föreslaget initialt kreditpaket som kapacitet för introduktion, testkörningar, kontroll och första perioden."
      },
      {
        key: "dynamicmail_billing_handoff",
        title: "Överlämning till DynamicMail",
        body:
          "Fakturering, betalning och konto- eller abonnemangshantering sker i DynamicMail efter inloggning och introduktionsöverlämning."
      },
      {
        key: "scope_control_boundary",
        title: "Gräns för kundnära kontroll",
        body:
          "SIE, städning, bokslut, lön, okända källor och riskmarkörer kan ändra åtkomstnivå, credits, omfattning och startdatum efter kontroll."
      }
    ],
    billingHandoffTitle: "Ingen faktureringsmutation i DynamicBooks",
    billingHandoffBody:
      "DynamicBooks får beskriva rekommendationer och visa icke-bindande uppskattningar. DynamicBooks ska inte skapa betalning, skriva abonnemangsstatus eller hantera kontoändringar.",
    nonBindingNote:
      "Abonnemang och Agent credits är preliminära rekommendationer. Slutlig åtkomst, kreditpaket och serviceplan bekräftas efter underlagskontroll.",
    guardrails: [
      "Ingen publik fast prislista som slutlig källa.",
      "Ingen betalning eller abonnemangsstatus skapas från DynamicBooks.",
      "Inga betalningsleverantörsskrivningar, faktureringshändelse-endpoints eller kontoändringar i marknadswebbplatsen.",
      "Komplexa ärenden styrs till kundnära kontroll innan fakturering eller service startar."
    ]
  },
  en: {
    eyebrow: "Access and agent credits",
    title: "Subscription gives access. Agent credits describe run capacity.",
    lead:
      "DynamicBooks shows a preliminary access tier and expected agent-credit intensity for data-room accounting. This is estimate positioning, not checkout or final billing.",
    cards: [
      {
        key: "subscription_access",
        title: "Subscription access",
        body:
          "The suggested DynamicMail tier reflects data rooms, workstreams, checkpoints, and accounting-agent intensity."
      },
      {
        key: "agent_credit_consumption",
        title: "Agent-credit consumption",
        body:
          "Monthly credits are shown in DynamicMail ledger-credit units and estimated from actual agent runs, transaction volume, VAT period, AP/AR, SIE, cleanup, year-end, and fixed assets."
      },
      {
        key: "estimate_output",
        title: "Estimate output",
        body:
          "The quote result shows suggested subscription tier, expected monthly credits, and an initial credit pack for onboarding, dry-runs, checkpoints, migration, and first-period learning."
      },
      {
        key: "dynamicmail_billing_handoff",
        title: "DynamicMail billing handoff",
        body:
          "Billing, checkout, and account/subscription management are handled in DynamicMail after login and onboarding handoff."
      },
      {
        key: "scope_control_boundary",
        title: "Scope control boundary",
        body:
          "SIE, cleanup, year-end, payroll, unknown sources, and risk markers can change tier, credits, scope, and start date after checks."
      }
    ],
    billingHandoffTitle: "No billing mutation in DynamicBooks",
    billingHandoffBody:
      "DynamicBooks may describe recommendations and show non-binding estimates. DynamicBooks must not create checkout, write subscription state, or manage account changes.",
    nonBindingNote:
      "Subscription and agent credits are preliminary recommendations. Final access, credit pack, and service plan are confirmed after evidence checks.",
    guardrails: [
      "No public fixed price list as source of truth.",
      "No checkout or subscription state is created from DynamicBooks.",
      "No payment-provider writes, billing-event endpoints, or account mutations in the marketing site.",
      "Complex cases route to scope control before billing or service starts."
    ]
  }
} satisfies Record<Locale, SubscriptionAgentCreditContent>;
